import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  adminGetProduct,
  adminUpdateProduct,
  adminCreateProduct,
  adminUploadImage,
  adminDeleteImage,
  adminReorderImages,
  adminUpdateImage,
  imgUrl,
} from "@/lib/api";

const CATEGORIES = [
  { key: "girls", label: "Girls" },
  { key: "boys", label: "Boys" },
  { key: "women", label: "Women" },
  { key: "men", label: "Teens" },
];
const MOTIFS = ["butterfly", "dragonfly", "owl", "lotus", "hearts", "car", "bee", "leaf", "bud"];
const ROLES = ["hero", "main", "back", "detail", "print", "pair", "lifestyle", "embroidery", "fabric", "button", "lining", "stitch", "sleeve"];

// Convert a product name into a URL-safe slug (lowercase, hyphens only)
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const emptyProduct = {
  slug: "",
  name: "",
  category: "girls",
  motif: "butterfly",
  price: 1199,
  sizes: [],
  age_range: "",
  hook: "",
  story: "",
  craft: "",
  care: "",
  images: [],
  pair_slug: "",
  pair_price: null,
  published: true,
  coming_soon: false,
};

const AdminProductEdit = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const isNew = slug === "new";

  const [product, setProduct] = useState(emptyProduct);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [imgDragIdx, setImgDragIdx] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false); // slug auto-syncs from name until user edits it
  const fileInput = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem("melavi_admin_token")) {
      nav("/admin/login");
      return;
    }
    if (isNew) return;
    (async () => {
      try {
        const p = await adminGetProduct(slug);
        setProduct({ ...emptyProduct, ...p, sizes: p.sizes || [], images: p.images || [] });
      } catch (err) {
        toast.error("Failed to load product");
        nav("/admin");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const set = (patch) => setProduct((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        // strip images (images live on server, added after create)
        const { images, pair_price, ...payload } = product;
        if (!payload.slug || !payload.name) {
          toast.error("Slug and name are required");
          setSaving(false);
          return;
        }
        await adminCreateProduct(payload);
        toast.success("Product created");
        nav(`/admin/product/${payload.slug}`);
      } else {
        const { slug: _s, id, created_at, ...patch } = product;
        // clean empty pair_price
        if (patch.pair_price === "" || patch.pair_price == null) delete patch.pair_price;
        await adminUpdateProduct(slug, patch);
        toast.success("Saved");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    if (isNew) {
      toast.error("Save the product first, then add images");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const { images } = await adminUploadImage(slug, file, "main", "");
        set({ images });
      }
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  const removeImage = async (url) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      const { images } = await adminDeleteImage(slug, url);
      set({ images });
      toast.success("Image removed");
    } catch (err) {
      toast.error("Could not remove");
    }
  };

  const patchImage = async (url, patch) => {
    try {
      const { images } = await adminUpdateImage(slug, { url, ...patch });
      set({ images });
    } catch (err) {
      toast.error("Could not update image");
    }
  };

  const reorder = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    const next = [...product.images];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    set({ images: next });
    try {
      const { images } = await adminReorderImages(slug, next.map((i) => i.url));
      set({ images });
    } catch {
      toast.error("Reorder failed — refresh to sync");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center" data-testid="admin-edit-loading">
        <div className="nav-item" style={{ color: "var(--stone)" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground pb-24" data-testid="admin-edit-page">
      {/* Sub-header */}
      <div className="hairline-b bg-ground/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div>
            <Link to="/admin" className="nav-item hover:text-ink transition-colors" style={{ color: "var(--stone)" }} data-testid="admin-back-link">
              ← All products
            </Link>
            <div className="serif text-2xl mt-1">{isNew ? "New product" : product.name || slug}</div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={save}
              disabled={saving}
              className="btn-solid"
              data-testid="admin-save-btn"
            >
              {saving ? "Saving…" : isNew ? "Create" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-12 pt-8 grid md:grid-cols-2 gap-16">
        {/* LEFT: fields */}
        <div>
          <h2 className="serif text-3xl mb-6">Details</h2>

          <Field label="Slug (URL)" required disabled={!isNew} hint={isNew ? "Auto-generated from name. Click to override." : "Locked once created."}>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: "var(--stone)" }}>melavi.in/product/</span>
              <input
                value={product.slug}
                disabled={!isNew}
                onChange={(e) => { setSlugTouched(true); set({ slug: slugify(e.target.value) }); }}
                placeholder="auto-fills as you type the name"
                className="flex-1 px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink disabled:opacity-60"
                data-testid="admin-field-slug"
              />
            </div>
          </Field>

          <Field label="Name" required>
            <input
              value={product.name}
              onChange={(e) => {
                const name = e.target.value;
                set(isNew && !slugTouched ? { name, slug: slugify(name) } : { name });
              }}
              className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
              data-testid="admin-field-name"
            />
          </Field>

          <div className="grid grid-cols-2 gap-6">
            <Field label="Category" hint="Same as the website: Girls · Boys · Women · Men. Twinning is set via the Pair slug below.">
              <select
                value={product.category}
                onChange={(e) => set({ category: e.target.value })}
                className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink capitalize"
                data-testid="admin-field-category"
              >
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>

            <Field label="Motif">
              <select
                value={product.motif}
                onChange={(e) => set({ motif: e.target.value })}
                className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink capitalize"
                data-testid="admin-field-motif"
              >
                {MOTIFS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Field label="Price (₹)">
              <input
                type="number"
                value={product.price}
                onChange={(e) => set({ price: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
                data-testid="admin-field-price"
              />
            </Field>

            <Field label="Age range">
              <input
                value={product.age_range || ""}
                onChange={(e) => set({ age_range: e.target.value })}
                placeholder="1–3 years"
                className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
                data-testid="admin-field-age"
              />
            </Field>
          </div>

          <Field label="Sizes (comma-separated)">
            <input
              value={(product.sizes || []).join(", ")}
              onChange={(e) => {
                const sizes = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                // seed missing sizes in stock with 6, drop removed sizes
                const cur = product.stock || {};
                const nextStock = {};
                sizes.forEach((s) => { nextStock[s] = cur[s] != null ? cur[s] : 6; });
                if (!sizes.length && !cur["one-size"]) nextStock["one-size"] = 6;
                if (!sizes.length && cur["one-size"] != null) nextStock["one-size"] = cur["one-size"];
                set({ sizes, stock: nextStock });
              }}
              placeholder="S, M, L, XL"
              className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
              data-testid="admin-field-sizes"
            />
          </Field>

          <div className="mt-10 mb-6 hairline-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="serif text-2xl">Stock on the table</h3>
              <span className="nav-item" style={{ color: "var(--stone)" }} data-testid="admin-stock-total-chip">
                Total: {Object.values(product.stock || {}).reduce((a, b) => a + (parseInt(b, 10) || 0), 0)} pieces
              </span>
            </div>
            <div className="hairline p-5 space-y-3" style={{ background: "rgba(28,26,25,0.03)" }} data-testid="admin-stock-grid">
              {(() => {
                const keys = (product.sizes && product.sizes.length)
                  ? product.sizes
                  : ["one-size"];
                return keys.map((k) => (
                  <div key={k} className="flex items-center gap-4">
                    <span className="nav-item w-28 uppercase" style={{ color: "var(--ink)" }}>{k}</span>
                    <input
                      type="number"
                      min="0"
                      value={(product.stock || {})[k] ?? 0}
                      onChange={(e) => {
                        const v = Math.max(0, parseInt(e.target.value, 10) || 0);
                        set({ stock: { ...(product.stock || {}), [k]: v } });
                      }}
                      className="w-28 px-3 py-2 hairline bg-ground focus:outline-none focus:border-ink text-lg serif"
                      data-testid={`admin-stock-${k}`}
                    />
                    <span
                      className={`text-xs tracking-widest uppercase ${((product.stock || {})[k] ?? 0) === 0 ? "text-[color:var(--accent)]" : ""}`}
                      style={((product.stock || {})[k] ?? 0) > 0 ? { color: "var(--stone)" } : {}}
                    >
                      {((product.stock || {})[k] ?? 0) === 0
                        ? "Sold out"
                        : ((product.stock || {})[k] ?? 0) <= 3
                          ? `Only ${(product.stock || {})[k]} left`
                          : `${(product.stock || {})[k]} on the table`}
                    </span>
                  </div>
                ));
              })()}
              <div className="text-xs pt-3 hairline-t" style={{ color: "var(--stone)" }}>
                Sizes are pulled from the field above. Sold-out sizes are disabled on the storefront automatically.
              </div>
            </div>
          </div>

          <Field label="Hook (1-line teaser)">
            <input
              value={product.hook}
              onChange={(e) => set({ hook: e.target.value })}
              className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
              data-testid="admin-field-hook"
            />
          </Field>

          <Field label="Story">
            <textarea
              value={product.story}
              onChange={(e) => set({ story: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink resize-y"
              data-testid="admin-field-story"
            />
          </Field>

          <Field label="Craft notes">
            <textarea
              value={product.craft}
              onChange={(e) => set({ craft: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink resize-y"
              data-testid="admin-field-craft"
            />
          </Field>

          <Field label="Care">
            <textarea
              value={product.care}
              onChange={(e) => set({ care: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink resize-y"
              data-testid="admin-field-care"
            />
          </Field>

          <div className="grid grid-cols-2 gap-6">
            <Field label="Twinning · pair slug" hint="Slug of the matching piece to make this a father-son / mother-daughter set. Leave blank if standalone.">
              <input
                value={product.pair_slug || ""}
                onChange={(e) => set({ pair_slug: e.target.value || null })}
                placeholder="crazy-car-white"
                className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
                data-testid="admin-field-pair-slug"
              />
            </Field>
            <Field label="Pair price (₹)" hint="Bundle price when the pair is bought together.">
              <input
                type="number"
                value={product.pair_price ?? ""}
                onChange={(e) => set({ pair_price: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 hairline bg-transparent focus:outline-none focus:border-ink"
                data-testid="admin-field-pair-price"
              />
            </Field>
          </div>

          <div className="flex items-center gap-8 mt-6">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="admin-field-published-wrap">
              <input
                type="checkbox"
                checked={!!product.published}
                onChange={(e) => set({ published: e.target.checked })}
                data-testid="admin-field-published"
              />
              <span className="nav-item">Published</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="admin-field-coming-wrap">
              <input
                type="checkbox"
                checked={!!product.coming_soon}
                onChange={(e) => set({ coming_soon: e.target.checked })}
                data-testid="admin-field-coming"
              />
              <span className="nav-item">Coming soon</span>
            </label>
          </div>
        </div>

        {/* RIGHT: images */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="serif text-3xl">Images</h2>
            <span className="nav-item" style={{ color: "var(--stone)" }}>
              {(product.images || []).length} plate{(product.images || []).length === 1 ? "" : "s"}
            </span>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInput.current?.click()}
            className={`hairline p-8 text-center cursor-pointer transition-colors ${dragOver ? "bg-ink/5 border-ink" : "hover:bg-ink/[0.02]"}`}
            data-testid="admin-upload-drop"
          >
            <div className="nav-item mb-2">
              {uploading ? "Uploading…" : "Drop images here or click to browse"}
            </div>
            <div className="text-xs" style={{ color: "var(--stone)" }}>
              PNG, JPG, WebP · max 15 MB · auto-converted to WebP, resized to 1800px
            </div>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
              data-testid="admin-upload-input"
            />
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4" data-testid="admin-images-grid">
            {(product.images || []).map((img, idx) => (
              <div
                key={img.url}
                draggable
                onDragStart={() => setImgDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); reorder(imgDragIdx, idx); setImgDragIdx(null); }}
                className="hairline group relative"
                data-testid={`admin-img-card-${idx}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-sand relative">
                  <img
                    src={imgUrl(img.url)}
                    alt={img.caption || ""}
                    className="w-full h-full object-cover"
                    onLoad={(e) => {
                      const el = e.currentTarget;
                      const dimEl = el.parentElement.querySelector('[data-role="dim"]');
                      if (dimEl && el.naturalWidth) dimEl.textContent = `${el.naturalWidth}×${el.naturalHeight}`;
                    }}
                  />
                  <span
                    data-role="dim"
                    className="absolute bottom-2 right-2 px-1.5 py-0.5 text-[10px] tracking-widest uppercase"
                    style={{ background: "rgba(28,26,25,0.72)", color: "#F6F1E9" }}
                  />
                  {idx === 0 && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] tracking-widest uppercase" style={{ background: "var(--accent)", color: "#F6F1E9" }}>
                      Cover
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[10px] tracking-widest uppercase mb-2 flex items-center justify-between" style={{ color: "var(--stone)" }}>
                    <span>#{idx + 1} · drag to reorder</span>
                  </div>
                  <label className="text-[10px] tracking-[0.24em] uppercase block mb-1" style={{ color: "var(--stone)" }}>Role</label>
                  <select
                    value={img.role || "main"}
                    onChange={(e) => patchImage(img.url, { role: e.target.value })}
                    className="w-full px-2 py-1.5 hairline bg-transparent text-sm mb-2 capitalize focus:outline-none focus:border-ink"
                    data-testid={`admin-img-role-${idx}`}
                    title="hero = shop card / product hero · main = default plinth photo · back / detail / print / lifestyle · embroidery / fabric / button / lining = hotspot macros"
                  >
                    {ROLES.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
                  </select>
                  <label className="text-[10px] tracking-[0.24em] uppercase block mb-1" style={{ color: "var(--stone)" }}>Description</label>
                  <input
                    defaultValue={img.caption || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (img.caption || "")) {
                        patchImage(img.url, { caption: e.target.value });
                      }
                    }}
                    placeholder="e.g. Front, hand-embroidered butterflies"
                    className="w-full px-2 py-1.5 hairline bg-transparent text-sm focus:outline-none focus:border-ink"
                    data-testid={`admin-img-caption-${idx}`}
                  />
                  <button
                    onClick={() => removeImage(img.url)}
                    className="mt-3 w-full text-xs tracking-widest uppercase py-1.5 hairline hover:bg-ink hover:text-ground transition-colors"
                    data-testid={`admin-img-remove-${idx}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {isNew && (
            <p className="mt-6 text-sm" style={{ color: "var(--stone)" }}>
              Save the product first, then you&apos;ll be able to upload images.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, required, children, disabled, hint }) => (
  <div className={`mb-6 ${disabled ? "opacity-70" : ""}`}>
    <label className="nav-item mb-2 block" style={{ color: "var(--stone)" }}>
      {label}{required && <span className="text-[color:var(--accent)] ml-1">*</span>}
    </label>
    {children}
    {hint && (
      <div className="mt-1.5 text-xs leading-relaxed" style={{ color: "var(--stone)" }}>{hint}</div>
    )}
  </div>
);

export default AdminProductEdit;
